import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { useVisibility } from '../hooks/useVisibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const navigate = useNavigate();
  const isVisible = useVisibility();

  useEffect(() => {
    if (!isVisible) return undefined;
    fetchUnreadCount();
    fetchRecentNotifications();
    
    // Refresh every 10 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchRecentNotifications();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [isVisible]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread`, {
        headers: getAuthHeaders()
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=3`, {
        headers: getAuthHeaders()
      });
      setRecentNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching recent notifications:', error);
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const created = new Date(date);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getPriorityEmoji = (priority) => {
    switch (priority) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '💙';
      case 'low': return '💡';
      default: return '📢';
    }
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setShowPreview(!showPreview)}
        className="relative p-2 hover:bg-gray-700 rounded-lg transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6 text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Preview Popup */}
      {showPreview && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowPreview(false)}
          ></div>
          
          {/* Popup */}
          <div className="absolute right-0 mt-2 w-80 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 z-50 max-h-96 overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-500" />
                <h3 className="text-white font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {recentNotifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400">No notifications</p>
                </div>
              ) : (
                <div>
                  {recentNotifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 border-b border-gray-700 hover:bg-gray-700/50 cursor-pointer transition-colors ${
                        notif.status === 'unread' ? 'bg-gray-700/30' : ''
                      }`}
                      onClick={() => {
                        setShowPreview(false);
                        navigate('/notifications');
                      }}
                    >
                      <div className="flex gap-2">
                        <span className="text-lg">{getPriorityEmoji(notif.priority)}</span>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium line-clamp-1">
                            {notif.title}
                          </p>
                          <p className="text-gray-400 text-xs mt-1 line-clamp-2">
                            {notif.message}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            {getTimeAgo(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-700 bg-gray-800">
              <button
                onClick={() => {
                  setShowPreview(false);
                  navigate('/notifications');
                }}
                className="w-full py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
              >
                View All Notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
